import { useLayoutEffect, useRef } from "react";
import { Button } from "@legal-500/legal500-design-system";
import styles from "./ImportSummaryBar.module.css";

type ImportSummaryBarProps = {
  summary: { sentence: string; counts?: string };
  canReview: boolean;
  onCancel: () => void;
  onReview: () => void;
};

const COUNT_PATTERN = /(\d+)(?= (?:organisations?|submissions?|rankings?|commentary)\b)/;

/** Splits the text so the numbers can be bold; the words and their order are untouched. */
function withBoldCounts(text: string) {
  return text
    .split(COUNT_PATTERN)
    .map((part, index) => (index % 2 === 1 ? <b key={`${index}-${part}`}>{part}</b> : part));
}

const CLEARANCE_PROPERTY = "--float-panel-clearance";

/** Tells the floating panel's host the bar's height, so the panel stops above the bar. */
function useFloatPanelClearance() {
  const barRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const bar = barRef.current;
    const host = bar?.closest<HTMLElement>("[data-float-host]");
    if (!bar || !host) return;
    const report = () =>
      host.style.setProperty(CLEARANCE_PROPERTY, `${bar.getBoundingClientRect().height}px`);
    report();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(report);
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      host.style.removeProperty(CLEARANCE_PROPERTY);
    };
  }, []);
  return barRef;
}

export function ImportSummaryBar({
  summary,
  canReview,
  onCancel,
  onReview,
}: ImportSummaryBarProps) {
  const barRef = useFloatPanelClearance();
  return (
    <div ref={barRef} className={styles.bar}>
      <div className={styles.summary} aria-live="polite">
        <span className={styles.line}>{withBoldCounts(summary.sentence)}</span>
        {summary.counts && <span className={styles.line}>{withBoldCounts(summary.counts)}</span>}
      </div>
      <div className={styles.actions}>
        <Button variant="outlined" color="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={onReview} disabled={!canReview}>
          Review import
        </Button>
      </div>
    </div>
  );
}
